import type { ReactElement, ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { readRoxyListenEnabled, writeRoxyListenEnabled } from '../live/roxy-prefs.js';
import { isRoxySpeechSupported } from '../live/useRoxyListener.js';

export interface RoxyContextValue {
  listenEnabled: boolean;
  listening: boolean;
  supported: boolean;
  setListenEnabled: (enabled: boolean) => void;
  reportListening: (listening: boolean) => void;
}

const RoxyContext = createContext<RoxyContextValue | null>(null);

export function RoxyProvider({ children }: { children: ReactNode }): ReactElement {
  const [listenEnabled, setListenEnabledState] = useState(readRoxyListenEnabled);
  const [listening, setListening] = useState(false);
  const supported = isRoxySpeechSupported();

  const setListenEnabled = useCallback((enabled: boolean) => {
    setListenEnabledState(enabled);
    writeRoxyListenEnabled(enabled);
    if (!enabled) {
      setListening(false);
    }
  }, []);

  const reportListening = useCallback((next: boolean) => {
    setListening(next);
  }, []);

  const value = useMemo(
    (): RoxyContextValue => ({
      listenEnabled,
      listening,
      supported,
      setListenEnabled,
      reportListening,
    }),
    [listenEnabled, listening, reportListening, setListenEnabled, supported],
  );

  return <RoxyContext.Provider value={value}>{children}</RoxyContext.Provider>;
}

export function useRoxy(): RoxyContextValue {
  const value = useContext(RoxyContext);
  if (!value) {
    throw new Error('useRoxy must be used within RoxyProvider');
  }
  return value;
}
